import { useEffect, useMemo, useRef, useState } from "react";
import { fileURL } from "../../../utils/fileURL";
import "./BrandFormModal.css";

function BrandFormModal({ isOpen, onClose, brandData, onSubmit }) {
  const isEdit = !!brandData;
  const initialForm = useMemo(
    () => ({
      nombre: brandData?.nombre || "",
      estado: brandData?.estado ?? 1,
    }),
    [brandData]
  );

  const [form, setForm] = useState(initialForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const objectUrlRef = useRef(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setSaving(false);
    setAlert(null);
    setImageFile(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (brandData?.url_image) setImagePreview(fileURL(brandData.url_image));
    else setImagePreview(null);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, initialForm, brandData]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const updateField = (e) => {
    const { name, value } = e.target;
    if (name === "estado") {
      setForm((p) => ({ ...p, [name]: Number(value) }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showAlert("error", "Por favor selecciona un archivo de imagen válido.");
      return;
    }

    const maxMb = 5;
    if (file.size > maxMb * 1024 * 1024) {
      showAlert("error", `La imagen es demasiado grande. Máximo ${maxMb}MB.`);
      return;
    }

    setImageFile(file);

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setImagePreview(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const cleanName = String(form.nombre || "").trim();
    if (!cleanName) {
      showAlert("error", "El nombre de la marca es obligatorio.");
      return;
    }

    const fd = new FormData();
    fd.append("nombre", cleanName);
    fd.append("estado", String(Number(form.estado)));
    if (imageFile) fd.append("imagen_marca", imageFile);

    setSaving(true);

    try {
      await onSubmit(fd);
      onClose?.();
    } catch (error) {
      console.error("Brand save failed:", error);
      showAlert("error", error?.message || "No se pudo guardar la marca.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div className="tienda-modal tienda-modal--shell" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tienda-modal__header">
          <div className="tienda-modal__header-text">
            <h2 className="tienda-modal__title">{isEdit ? "Editar marca" : "Crear marca"}</h2>
            <p className="tienda-modal__subtitle">Define el nombre, estado e imagen de la marca.</p>
          </div>
          <button
            type="button"
            className="tienda-modal__close"
            onClick={onClose}
            disabled={saving}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        {alert && (
          <div className={`tienda-modal__alert tienda-modal__alert--${alert.type === "error" ? "error" : "info"}`}>
            <span>{alert.message}</span>
            <button
              type="button"
              className="tienda-modal__alert-x"
              onClick={() => setAlert(null)}
              disabled={saving}
              title="Cerrar alerta"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} className="tienda-modal__body">
          <div className="tienda-modal__field">
            <label className="tienda-modal__label">Nombre de la marca</label>
            <input
              type="text"
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              className="tienda-modal__input"
              required
              disabled={saving}
              autoFocus
            />
          </div>

          <div className="tienda-modal__field">
            <label className="tienda-modal__label">Estado</label>
            <select
              name="estado"
              value={Number(form.estado)}
              onChange={updateField}
              className="tienda-modal__select"
              disabled={saving}
            >
              <option value={1}>Activo</option>
              <option value={0}>Inactivo</option>
            </select>
          </div>

          <div className="tienda-modal__field">
            <label className="tienda-modal__label">Imagen de la marca</label>

            <div
              className={`tienda-modal__upload ${saving ? "is-disabled" : ""}`}
              onClick={() => {
                if (saving) return;
                document.getElementById("brand-img-input")?.click();
              }}
              title="Clic para subir imagen de la marca"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="vista previa" />
              ) : (
                <div className="tienda-modal__upload-placeholder">Clic para subir imagen</div>
              )}
            </div>

            <input
              id="brand-img-input"
              type="file"
              accept="image/*"
              onChange={handleImage}
              style={{ display: "none" }}
              disabled={saving}
            />
          </div>

          <div className="tienda-modal__footer">
            <button
              type="button"
              className="tienda-modal__btn tienda-modal__btn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="tienda-modal__btn tienda-modal__btn--primary"
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : isEdit
                ? "Guardar cambios"
                : "Crear marca"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BrandFormModal;
